import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import StopOutlined from '@mui/icons-material/StopOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { memo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import type { MeResponse } from '../../api';
import { FreshnessIndicator } from '../../components/FreshnessIndicator';
import { visuallyHidden } from '../../components/visually-hidden';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';
import { useFreshness } from '../shared/use-freshness';

import { AutomaticModeSuggestions } from './AutomaticModeSuggestions';
import { ChannelList } from './ChannelList';
import { DailyLimitAlert } from './DailyLimitAlert';
import { PositionsPreview } from './PositionsPreview';
import { StatusCard } from './StatusCard';
import { homeLinks } from './links';
import { useMe } from './use-me';

/**
 * Screen E6 · Home (specs/M1-home/spec.md; US-09). Fetches `GET /me`, refreshes it every 5 s while
 * visible (specs/M7-refresh/spec.md), and renders loading, failure or the account state with how
 * current it is. A failed refresh keeps the last state on screen under the Offline banner (M7 R7).
 */
export function HomeScreen(): ReactElement {
  const { t } = useTranslation();
  const me = useMe();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pb: 1 }}>
      <Header />
      {/* Kept for assistive tech: the wordmark is the visible title (mockup 1). */}
      <Typography component="h1" sx={visuallyHidden}>
        {t('home.title')}
      </Typography>
      {me.status === 'pending' ? (
        <Box
          role="status"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary', py: 2 }}
        >
          <CircularProgress size={20} aria-hidden />
          {t('home.loading')}
        </Box>
      ) : me.status === 'error' ? (
        <LoadFailed error={me.error} onRetry={me.refetch} />
      ) : (
        <HomeView
          me={me.data}
          receivedAt={me.receivedAt}
          refreshing={me.refreshing}
          refreshError={me.refreshError}
        />
      )}
    </Box>
  );
}

/** FXMate wordmark and the account shortcut of mockup 1. */
function Header(): ReactElement {
  const { t } = useTranslation();
  const title = t('app.title');
  return (
    <Box
      component="header"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}
    >
      {/* The brand name is not translated; "Mate" takes the brand colour, as on the mockups. */}
      <Box aria-hidden sx={{ fontWeight: 700, fontSize: 20, letterSpacing: '-0.01em' }}>
        {title.slice(0, 2)}
        <Box component="span" sx={{ color: 'primary.main' }}>
          {title.slice(2)}
        </Box>
      </Box>
      <IconButton
        component={RouterLink}
        to={homeLinks.account}
        aria-label={t('tabs.account')}
        sx={(theme) => ({
          width: 44,
          height: 44,
          borderRadius: 3,
          border: `1px solid ${theme.fx.border}`,
          bgcolor: 'background.paper',
          color: 'text.primary',
        })}
      >
        <SettingsOutlined aria-hidden />
      </IconButton>
    </Box>
  );
}

/** Failure of `GET /me`: only the translated reason, never a raw code or message (I9, I12). */
function LoadFailed({ error, onRetry }: { error: ApiError; onRetry: () => void }): ReactElement {
  const { t } = useTranslation();
  // params fill placeholders such as {{minutes}}; reasonMessage drops undeclared ones (C2 R6).
  const message = reasonMessage(error.reasonCode, error.params);
  return (
    <Alert
      severity="error"
      sx={(theme) => ({ bgcolor: theme.fx.dangerBg, color: theme.fx.text })}
      action={
        <Button color="inherit" size="small" onClick={onRetry} sx={{ minHeight: 36 }}>
          {t('home.retry')}
        </Button>
      }
    >
      {t(message.key, message.values)}
    </Alert>
  );
}

/**
 * The data part of Home: the freshness line, then the body. The body is memoised so the
 * `refreshing` flag, which flips twice per 5 s refresh, re-renders only the indicator.
 */
function HomeView({
  me,
  receivedAt,
  refreshing,
  refreshError,
}: {
  me: MeResponse;
  receivedAt: number;
  refreshing: boolean;
  refreshError: ApiError | undefined;
}): ReactElement {
  const freshness = useFreshness({ asOf: me.generatedAt, receivedAt, refreshing, refreshError });
  return (
    <>
      <FreshnessIndicator {...freshness} />
      <HomeBody me={me} />
    </>
  );
}

/** Account state, limits, emergency stop, suggestions, channels and positions (mockup 1). */
const HomeBody = memo(function HomeBody({ me }: { me: MeResponse }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <StatusCard me={me} />
      <DailyLimitAlert dailyLoss={me.status.status === 'incomplete' ? null : me.dailyLoss} />
      {/* FXM-02 §7: the emergency stop is one gesture from Home, in every state (M1 R12, AC13). */}
      <Button
        component={RouterLink}
        to={homeLinks.emergencyStop}
        variant="outlined"
        color="error"
        size="large"
        startIcon={<StopOutlined aria-hidden />}
        sx={{ minHeight: 52 }}
      >
        {t('home.emergencyStop')}
      </Button>
      <AutomaticModeSuggestions suggestions={me.automaticModeSuggestions} />
      <ChannelList channels={me.channels} />
      <PositionsPreview openPositions={me.openPositions} />
    </>
  );
});
